import { useMutation, useQuery } from '@tanstack/react-query';
import { CheckCircle2Icon, MessageSquareTextIcon, ShieldCheckIcon } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { ErrorState } from '@/components/ErrorState';
import { LegalLink } from '@/components/LegalLink';
import { LoadingBlock } from '@/components/LoadingBlock';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Field } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AuthLayout } from '@/layouts/AuthLayout';
import { api, errorMessage, isApiError } from '@/lib/api';
import { useBranding } from '@/lib/branding';
import type { ConsentPageView } from '@/lib/types';
import { cn } from '@/lib/utils';

export type ConsentMode = 'opt-in' | 'opt-out';

/**
 * Public enrollment page (/opt-in) and opt-out page (/opt-out), the ones a 10DLC campaign registration points at.
 * Enrolling puts a person on a job site's roster. Text messages are a separate yes-or-no choice made with two boxes
 * ("Yes, text me" / "No, do not text me"), neither pre-selected. The mobile number field is always on the page and
 * never required: the number is sent only with a ticked "Yes". Blank boxes or "No" complete enrollment with no number
 * and no consent, because carriers reject flows where consent is a condition of using the service. "Yes" creates the contact and a consent record (wording, time, address) and sends
 * a confirmation text. Opting out works for any number and stops texts from every organization number.
 */
export function SmsConsentPage({ mode }: { mode: ConsentMode }) {
  const { branding } = useBranding();
  const view = useQuery({ queryKey: ['consent', 'page'], queryFn: () => api.tenant.consentPage(), staleTime: 60_000 });

  const other = mode === 'opt-in' ? { to: '/opt-out', label: 'Need to stop texts? Opt out' } : { to: '/opt-in', label: 'Changed your mind? Enroll again' };
  const footer = (
    <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
      <Link to={other.to} className="hover:text-foreground underline underline-offset-4">
        {other.label}
      </Link>
      <LegalLink href={view.data?.privacyUrl} fallback="/legal/privacy" className="hover:text-foreground underline underline-offset-4">
        Privacy policy
      </LegalLink>
      <LegalLink href={view.data?.termsUrl} fallback="/legal/terms" className="hover:text-foreground underline underline-offset-4">
        Terms
      </LegalLink>
    </span>
  );
  const tenantLine = <>Text messages are optional. Message frequency varies. Message and data rates may apply. Reply STOP to cancel, HELP for help.</>;
  const eyebrow = mode === 'opt-in' ? 'Job-site enrollment' : 'Text messages';

  if (view.isPending) {
    return (
      <AuthLayout demo={false} wide eyebrow={eyebrow} title={mode === 'opt-in' ? 'Enroll at your job site' : 'Stop job-site texts'} footer={footer} tenantLine={tenantLine}>
        <LoadingBlock rows={4} />
      </AuthLayout>
    );
  }
  if (view.isError) {
    return (
      <AuthLayout demo={false} wide eyebrow={eyebrow} title={mode === 'opt-in' ? 'Job-site enrollment' : 'Text messages'} footer={footer} tenantLine={tenantLine}>
        <ErrorState error={view.error} onRetry={() => void view.refetch()} />
      </AuthLayout>
    );
  }
  const v = view.data;
  const org = v.tenantName || branding.tenantName;

  if (mode === 'opt-out') {
    return (
      <AuthLayout demo={false} wide eyebrow={eyebrow} title={`Stop texts from ${org}`} description="Enter the mobile number that receives the texts. It stops for every job site at once, and takes effect right away." footer={footer} tenantLine={tenantLine}>
        <OptOutForm view={v} />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      demo={false}
      wide
      eyebrow={eyebrow}
      title={`Enroll at your ${org} job site`}
      description={v.description || `Tell ${org} which job site you're on and how to reach you. Texts about ${v.programName} are optional: you can enroll without them, and add or stop them at any time.`}
      footer={footer}
      tenantLine={tenantLine}
    >
      {v.enabled ? (
        <EnrollForm view={v} />
      ) : (
        <div className="space-y-4 text-sm">
          <Alert>
            <MessageSquareTextIcon aria-hidden />
            <AlertDescription>
              {org} is not accepting new SMS sign-ups through this page right now. Please contact your supervisor or project contact for information about when enrollment will reopen.
            </AlertDescription>
          </Alert>
          <p className="text-muted-foreground">
            Already getting texts you do not want?{' '}
            <Link to="/opt-out" className="text-foreground underline underline-offset-4">
              Opt out here
            </Link>
            .
          </p>
        </div>
      )}
    </AuthLayout>
  );
}

function fieldErrors(err: unknown): Record<string, string> {
  return isApiError(err) && err.status === 422 ? err.fieldErrors() : {};
}

type SmsChoice = 'yes' | 'no';

function EnrollForm({ view }: { view: ConsentPageView }) {
  const [jobId, setJobId] = useState(view.jobs[0]?.id ?? '');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [language, setLanguage] = useState(view.defaultLanguage);
  const [phone, setPhone] = useState('');
  // One yes-or-no answer made with two separate boxes, neither pre-selected. The number field is always shown and only
  // matters for "yes"; blank boxes enroll with no consent.
  const [smsChoice, setSmsChoice] = useState<SmsChoice | null>(null);
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (!view.jobs.some((j) => j.id === jobId)) setJobId(view.jobs[0]?.id ?? '');
  }, [view.jobs, jobId]);

  const submit = useMutation({
    mutationFn: (smsConsent: boolean) => api.tenant.enroll({ jobId, firstName: firstName.trim(), lastName: lastName.trim(), phone: smsConsent ? phone.trim() : undefined, language, smsConsent }),
  });
  const errors = fieldErrors(submit.error);
  const namesOk = firstName.trim().length > 0 && lastName.trim().length > 0;
  const phoneOk = phone.trim().length >= 7;
  const jobName = view.jobs.find((j) => j.id === jobId)?.name ?? '';
  // The stored disclosure ends with the sentence about the two documents; on the page that sentence carries the links.
  const disclosureBody = view.disclosure.replace(/\s*See our Privacy Policy and Terms\.?\s*$/i, '').trim();
  const choose = (c: SmsChoice, on: boolean) => setSmsChoice(on ? c : null);

  if (submit.isSuccess) {
    const r = submit.data;
    return (
      <div className="space-y-5 text-sm">
        <div className="flex items-start gap-3">
          <CheckCircle2Icon className="text-primary mt-0.5 size-6 shrink-0" aria-hidden />
          <div>
            {r.smsConsent ? (
              <>
                <p className="text-base font-medium">You&apos;re on the {r.jobName} roster and signed up for texts.</p>
                <p className="text-muted-foreground mt-1">
                  {r.confirmationSent ? <>We just sent a confirmation text to {r.phoneDisplay}. </> : <>Texts for this job site will go to {r.phoneDisplay}. </>}
                  Reply HELP for help or STOP to opt out.
                </p>
              </>
            ) : (
              <>
                <p className="text-base font-medium">You&apos;re on the {r.jobName} roster.</p>
                <p className="text-muted-foreground mt-1">
                  {smsChoice === 'no' ? 'You chose not to receive text messages, so none will be sent to you.' : 'You did not tick "Yes, text me", so no text messages will be sent to you and your mobile number was not used.'} Updates for your site come from your project team. Want text updates later? Come back to this page any time, enter your mobile number and tick &ldquo;Yes, text me&rdquo;.
                </p>
              </>
            )}
          </div>
        </div>
        {r.smsConsent && (
          <p className="text-muted-foreground text-xs">
            Your consent was recorded on {new Date().toLocaleDateString(undefined, { dateStyle: 'long' })} together with the wording you agreed to, so {view.tenantName} can show it on request.
          </p>
        )}
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            submit.reset();
            setPhone('');
            setFirstName('');
            setLastName('');
            setSmsChoice(null);
            setTouched(false);
          }}
        >
          Enroll another person
        </Button>
      </div>
    );
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!jobId || !namesOk) return;
    if (smsChoice === 'yes' && !phoneOk) return;
    submit.mutate(smsChoice === 'yes');
  };
  const choiceError = errors['smsConsent'];
  const boxClass = (c: SmsChoice) => cn('border-input flex cursor-pointer items-start gap-3 border px-4 py-3 text-sm leading-relaxed', smsChoice === c && 'border-primary ring-primary bg-primary/5 ring-1');

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
      {view.jobs.length > 1 ? (
        <Field label="Job site" htmlFor="c-job" error={errors['jobId']}>
          <Select value={jobId} onValueChange={setJobId}>
            <SelectTrigger id="c-job" className="w-full">
              <SelectValue placeholder="Choose your job site" />
            </SelectTrigger>
            <SelectContent>
              {view.jobs.map((j) => (
                <SelectItem key={j.id} value={j.id}>
                  {j.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      ) : (
        <div className="bg-muted/40 flex items-center gap-3 border px-4 py-3 text-sm">
          <ShieldCheckIcon className="text-muted-foreground size-4 shrink-0" aria-hidden />
          <span>
            Job site: <span className="font-medium">{jobName}</span>
          </span>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="First name" htmlFor="c-first" error={errors['firstName'] ?? (touched && !firstName.trim() ? 'Enter your first name' : undefined)}>
          <Input id="c-first" autoComplete="given-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} maxLength={100} required />
        </Field>
        <Field label="Last name" htmlFor="c-last" error={errors['lastName'] ?? (touched && !lastName.trim() ? 'Enter your last name' : undefined)}>
          <Input id="c-last" autoComplete="family-name" value={lastName} onChange={(e) => setLastName(e.target.value)} maxLength={100} required />
        </Field>
      </div>

      {view.languages.length > 1 && (
        <Field label="Preferred language" htmlFor="c-lang" description="Messages are sent to you in this language when translation is on.">
          <Select value={language} onValueChange={setLanguage}>
            <SelectTrigger id="c-lang" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {view.languages.map((l) => (
                <SelectItem key={l.code} value={l.code}>
                  {l.name} · {l.nativeName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      )}

      <fieldset className="border-input flex flex-col gap-3 border px-4 pt-2 pb-4" aria-describedby="c-sms-help">
        <legend className="px-1 text-sm font-medium">
          Text message updates <span className="text-muted-foreground font-normal">· optional</span>
        </legend>
        <p id="c-sms-help" className="text-muted-foreground text-sm">
          Want {view.programName} by text message? Enter your mobile number and tick &ldquo;Yes&rdquo;. Leave the boxes blank, or tick &ldquo;No&rdquo;, and you still enroll with no texts.
        </p>

        <Field label="Mobile number" htmlFor="c-phone" description='Optional. Only used if you tick "Yes, text me" below; not needed to enroll.' error={errors['phone'] ?? (touched && smsChoice === 'yes' && !phoneOk ? 'Enter the mobile number that should receive texts, or untick "Yes, text me" to enroll without texts' : undefined)}>
          <Input id="c-phone" type="tel" inputMode="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={view.defaultCountry === 'US' ? '(555) 555-0123' : 'Mobile number'} />
        </Field>

        <label className={boxClass('yes')}>
          <Checkbox checked={smsChoice === 'yes'} onCheckedChange={(c) => choose('yes', c === true)} className="mt-0.5" aria-describedby="c-disclosure" />
          <span className="text-foreground/90">
            <span className="block font-medium">
              Yes, text me {view.programName} updates. <span className="text-muted-foreground font-normal">(optional)</span>
            </span>
            <span id="c-disclosure" className="text-muted-foreground block">
              {disclosureBody} See our{' '}
              <LegalLink href={view.privacyUrl} fallback="/legal/privacy" className="underline underline-offset-4">
                Privacy Policy
              </LegalLink>{' '}
              and{' '}
              <LegalLink href={view.termsUrl} fallback="/legal/terms" className="underline underline-offset-4">
                Terms
              </LegalLink>
              .
            </span>
          </span>
        </label>
        <label className={boxClass('no')}>
          <Checkbox checked={smsChoice === 'no'} onCheckedChange={(c) => choose('no', c === true)} className="mt-0.5" />
          <span className="text-foreground/90">
            <span className="block font-medium">No, do not text me.</span>
            <span className="text-muted-foreground block">Enroll me without text messages. Nothing will be sent to me and a mobile number is not needed. I can sign up for texts later if I want them.</span>
          </span>
        </label>
        {choiceError && (
          <p className="text-destructive text-sm" role="alert">
            {choiceError}
          </p>
        )}
      </fieldset>

      {submit.isError && Object.keys(errors).length === 0 && (
        <Alert variant="destructive">
          <AlertDescription>{errorMessage(submit.error)}</AlertDescription>
        </Alert>
      )}

      <Button type="submit" disabled={submit.isPending} className="w-full">
        {submit.isPending ? 'Enrolling…' : smsChoice === 'yes' ? 'Enroll and get texts' : smsChoice === 'no' ? 'Enroll without text messages' : 'Enroll'}
      </Button>
      <p className="text-muted-foreground text-xs">
        Text message consent is not required to enroll or to use any {view.tenantName} service. You are enrolled with no texts if you leave both boxes blank or tick &ldquo;No, do not text me&rdquo;, and your mobile number is not used. If you tick &ldquo;Yes&rdquo;, your consent is recorded with the wording above, the date and time and the address you enrolled from, and a confirmation text is sent right away.
      </p>
    </form>
  );
}

function OptOutForm({ view }: { view: ConsentPageView }) {
  const [phone, setPhone] = useState('');
  const [touched, setTouched] = useState(false);
  const submit = useMutation({ mutationFn: () => api.tenant.optOut({ phone: phone.trim() }) });
  const errors = fieldErrors(submit.error);

  if (submit.isSuccess) {
    return (
      <div className="space-y-5 text-sm">
        <div className="flex items-start gap-3">
          <CheckCircle2Icon className="text-primary mt-0.5 size-6 shrink-0" aria-hidden />
          <div>
            <p className="text-base font-medium">Done. {submit.data.phoneDisplay} will no longer receive texts from {view.tenantName}.</p>
            <p className="text-muted-foreground mt-1">This covers every job site. If you want to receive messages again, enroll again on the {view.tenantName} enrollment page and tick the text-message box.</p>
          </div>
        </div>
        <Button asChild variant="outline">
          <Link to="/opt-in">Enroll again</Link>
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setTouched(true);
        if (phone.trim().length < 7) return;
        submit.mutate();
      }}
      className="flex flex-col gap-5"
      noValidate
    >
      <Field label="Mobile number" htmlFor="o-phone" error={errors['phone'] ?? (touched && phone.trim().length < 7 ? 'Enter the mobile number that gets the texts' : undefined)}>
        <Input id="o-phone" type="tel" inputMode="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={view.defaultCountry === 'US' ? '(555) 555-0123' : 'Mobile number'} required />
      </Field>
      {submit.isError && Object.keys(errors).length === 0 && (
        <Alert variant="destructive">
          <AlertDescription>{errorMessage(submit.error)}</AlertDescription>
        </Alert>
      )}
      <Button type="submit" variant="destructive" disabled={submit.isPending} className="w-full">
        {submit.isPending ? 'Stopping texts…' : 'Stop texting this number'}
      </Button>
      <p className="text-muted-foreground text-xs">You can also reply STOP to any message from {view.tenantName}. Replying HELP gets you assistance.</p>
    </form>
  );
}
